import Button from 'components/Button';
import FormattedText from 'components/FormattedText';
import Icon from 'components/Icon';
import { i18nPath } from 'config/i18nNs';
import useProjectInfo from 'hooks/useProjectInfo';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import type { HallOfFameType } from 'types/policyPages';
import { Body1, Heading4 } from 'typography';
import { projectId } from 'utils/urlQueries';
import { getPathInfo } from 'utils/versionUtils';
import styles from './textBlock.module.scss';

export interface TextBlockProps {
  sectionI18nKey?: string;
  index: number;
  setActiveTab: (tab: string) => void;
  activities?: Array<{ [key: string]: string }>;
  cookieTypes?: Array<{ [key: string]: string }>;
}

const TextBlock: React.FC<TextBlockProps> = ({
  index,
  sectionI18nKey,
  setActiveTab,
  activities,
  cookieTypes
}) => {
  const { t } = useTranslation();
  const projectInfo = useProjectInfo();
  const history = useHistory();
  const { locPath } = getPathInfo();

  const section: {
    anchor?: string;
    title: string;
    content: string;
    listActivities?: boolean;
    listCookies?: boolean;
    hallOfFame?: HallOfFameType;
  } = t(`${sectionI18nKey}`);
  const content = t(`${sectionI18nKey}.content`, projectInfo);

  const displayActivity = (activityId: string) => {
    history.push(`/${locPath}/privacy/${activityId}?id=${projectId}`);
    setActiveTab(activityId);
  };

  const displayCookieType = (tabId: string) => {
    history.push(`/${locPath}/cookies/${tabId}?id=${projectId}`);
    setActiveTab(tabId);
  };

  return (
    <article key={`article${index}`} className={styles.article}>
      <Heading4>
        <a id={section.anchor} />
        {section.title}
      </Heading4>

      <FormattedText i18nKey={content} id={`text-${section.anchor}`} />

      {section.listActivities && (
        <ul className={styles.activitiesList}>
          {activities &&
            Array.isArray(activities) &&
            activities.map((item) => (
              <li
                key={item.tabId}
                onClick={() => displayActivity(item.tabId)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && displayActivity(item.tabId)
                }
                tabIndex={0}
              >
                <Body1 hasIcon className="underline">
                  <Icon name="icon-diagonal-up-right" size={18} />
                  {item.label}
                </Body1>
              </li>
            ))}
        </ul>
      )}

      {section.listCookies && (
        <div className={styles.details}>
          {cookieTypes &&
            Array.isArray(cookieTypes) &&
            cookieTypes.map((item, index) => (
              <div key={item.title} className={styles.topic}>
                <Heading4>{item.title}</Heading4>
                <FormattedText i18nKey={item.intro} />
                <div
                  className={styles.moreInfo}
                  onClick={() => displayCookieType(item.tabId)}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && displayCookieType(item.tabId)
                  }
                  tabIndex={0}
                >
                  <Button variant="secondary">
                    {t(`${i18nPath.dictionary}.knowMoreCookies`)}
                  </Button>
                </div>
              </div>
            ))}
        </div>
      )}

      {section.hallOfFame &&
        Array.isArray(section.hallOfFame.view.items) &&
        section.hallOfFame?.view?.items?.map((item) => (
          <div key={item.name} className={styles.hofContainer}>
            <Body1 className={styles.hallOfFameName}>{item?.name}</Body1>
            <Body1 hasIcon>
              <Icon name="icon-survey-trophy" size={18} />
              {item?.description}
            </Body1>
            <Body1 hasIcon>
              <Icon name="icon-calendar1" size={18} />
              {moment(item?.date).format('DD/MM/YY')}
            </Body1>
          </div>
        ))}
    </article>
  );
};

export default TextBlock;
